/**
 * Profile picture URL for any email, built in the browser.
 *
 * fir-user stores a picture at `up/<key>/p.png` in the public logo bucket, where the key is the
 * lowercased email plus a salt, each character shifted by 4 and non-word characters dropped
 * (fir-user / fir-login / fir-claimsmgmt `UrlUtility.getEncodedString`). A user without a picture
 * gets a URL that answers 403; avatar components fall back to initials on the load error.
 *
 * QA's bucket is cowlogos-qa, so QA shows initials.
 */
const AVATAR_URL_PREFIX = 'https://cowlogos.s3-us-west-2.amazonaws.com/up/';
const AVATAR_SALT = 'morecowbell';

const keys = new Map<string, string>();

/** Port of Java `UrlUtility.getEncodedString`. JS and Java remainders both keep the dividend's sign. */
export function encodeAvatarKey(text: string): string {
  let shifted = '';
  for (let i = 0; i < text.length; i += 1) {
    const code = text.charCodeAt(i);
    // Java Character.isUpperCase: a cased character that lowercases to something else.
    const isUpperCase = text[i] !== text[i].toLowerCase();
    const base = isUpperCase ? 65 : 97;
    shifted += String.fromCharCode(((code + 4 - base) % 26) + base);
  }
  return shifted.replace(/\W/g, '');
}

/** The encoded S3 key for an email, memoised. Null without an email. */
export function getAvatarKey(email?: string | null): string | null {
  const normalized = email?.trim().toLowerCase();
  if (!normalized) {
    return null;
  }
  let key = keys.get(normalized);
  if (key === undefined) {
    key = encodeAvatarKey(normalized + AVATAR_SALT);
    keys.set(normalized, key);
  }
  return key;
}

export function getAvatarUrl(email?: string | null): string | null {
  const key = getAvatarKey(email);
  return key ? `${AVATAR_URL_PREFIX}${key}/p.png` : null;
}

/** Production's public up/ folder; the staff list names the folder for the environment. */
export const DEFAULT_AVATAR_BASE_URL = AVATAR_URL_PREFIX;

/** Encoded key -> photo version (epoch seconds). Lists only the staff who have a photo. */
export type StaffAvatarManifest = Readonly<Record<string, number>>;

/**
 * The staff thumbnail (96px t.jpg, written by fir-login) to load, or null when the manifest
 * says there is no photo.
 *
 * A null here means no request at all: S3 answers a missing photo with a 403 the browser
 * cannot cache, so the only way to avoid one is not to ask. The version in the query string
 * changes when the photo does, which lets the thumbnail be cached as immutable.
 */
export function avatarSrc(
  email: string | null | undefined,
  manifest: StaffAvatarManifest,
  baseUrl: string = DEFAULT_AVATAR_BASE_URL
): string | null {
  const key = getAvatarKey(email);
  if (!key || !Object.prototype.hasOwnProperty.call(manifest, key)) {
    return null;
  }
  return `${baseUrl}${key}/t.jpg?v=${manifest[key]}`;
}

/** What fir-login's GET /v1/users/avatars returns: the bucket's up/ folder and who has a photo. */
export interface StaffAvatarList {
  readonly baseUrl: string;
  readonly avatars: StaffAvatarManifest;
}
